/* eslint-disable indent */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Mic, Building2, BedDouble } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/app/context/language-context';
import { PropertyService } from '@/app/components/property-service';

// Mock data for filters
const propertyTypes = [
  { value: '', label: 'All Types' },
  { value: 'AP', label: 'Apartment' },
  { value: 'VH', label: 'Villa' },
  { value: 'TH', label: 'Townhouse' },
  { value: 'PH', label: 'Penthouse' },
  { value: 'LP', label: 'Land' },
  { value: 'OF', label: 'Office' },
  { value: 'RE', label: 'Retail' },
  { value: 'SH', label: 'Shop' },
  { value: 'WH', label: 'Warehouse' },
];

const bedOptions = ['Studio', '1', '2', '3', '4', '5', '6+'];
const bathOptions = ['1', '2', '3', '4', '5+'];

export default function PropertySearch({ NoResultsComponent }) {
  const router = useRouter();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [activeTab, setActiveTab] = useState('Rent');
  const [isListening, setIsListening] = useState(false);
  const [showPropertyTypes, setShowPropertyTypes] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [selectedBeds, setSelectedBeds] = useState([]);
  const [selectedBaths, setSelectedBaths] = useState([]);
  const [showBedsBaths, setShowBedsBaths] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const searchContainerRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const bedsBathsRef = useRef(null);
  const inputRef = useRef(null);

  // Voice recognition setup
  useEffect(() => {
    let recognition;

    if (typeof window !== 'undefined' && 'webkitSpeechRecognition' in window) {
      recognition = new window.webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      // Store recognition instance
      window.recognition = recognition;
    }

    return () => {
      if (recognition) {
        recognition.abort();
      }
    };
  }, []);

  const startListening = () => {
    if (window.recognition) {
      setIsListening(true);
      try {
        window.recognition.start();
      } catch (error) {
        console.error('Speech recognition error:', error);
        setIsListening(false);
      }
    } else {
      alert(
        'Voice search is not supported in your browser. Please try Chrome or Edge.',
      );
    }
  };

  // Search results logic
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.length > 0) {
        setIsLoading(true);
        try {
          // Get property results
          const properties = await PropertyService.searchProperties(
            searchQuery,
            selectedPropertyType,
            selectedBeds,
            selectedBaths,
            activeTab.toLowerCase(),
          );

          // Format properties for display
          const formattedProperties = properties
            .map((property) =>
              PropertyService.formatPropertyForDisplay(property),
            )
            .slice(0, 20); // Limit to 5 results for dropdown

          setSearchResults(formattedProperties);

          // Get location suggestions
          const locations =
            await PropertyService.getLocationSuggestions(searchQuery);
          setLocationSuggestions(locations);
        } catch (error) {
          console.error('Error fetching search results:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
        setLocationSuggestions([]);
      }
    };

    // Use debounce to avoid too many API calls
    const debounceTimer = setTimeout(() => {
      fetchSearchResults();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [
    searchQuery,
    selectedPropertyType,
    selectedBeds,
    selectedBaths,
    activeTab,
  ]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsSearchFocused(false);
      }
      if (
        propertyTypeRef.current &&
        !propertyTypeRef.current.contains(event.target)
      ) {
        setShowPropertyTypes(false);
      }
      if (
        bedsBathsRef.current &&
        !bedsBathsRef.current.contains(event.target)
      ) {
        setShowBedsBaths(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSelection = (option, type) => {
    if (type === 'bed') {
      setSelectedBeds((prev) =>
        prev.includes(option)
          ? prev.filter((b) => b !== option)
          : [...prev, option],
      );
    } else {
      setSelectedBaths((prev) =>
        prev.includes(option)
          ? prev.filter((b) => b !== option)
          : [...prev, option],
      );
    }
  };

  // Function to format selected values
  const formatSelection = (selectedValues, label) => {
    if (selectedValues.length === 0) return `${t('select')} ${label}`;

    // Sort numbers while keeping "Studio" & "5+" intact
    const sortedValues = selectedValues
      .filter((v) => v !== '5+')
      .sort((a, b) => (isNaN(a) ? -1 : a - b)); // Keeps "Studio" at the beginning

    const formatted = [];
    let tempRange = [];

    sortedValues.forEach((value, index) => {
      const num = isNaN(value) ? value : Number(value);

      if (index === 0 || num - sortedValues[index - 1] === 1) {
        tempRange.push(num);
      } else {
        formatted.push(
          tempRange.length > 1
            ? `${tempRange[0]}-${tempRange[tempRange.length - 1]}`
            : tempRange[0],
        );
        tempRange = [num];
      }
    });

    // Add last range or single value
    if (tempRange.length) {
      formatted.push(
        tempRange.length > 1
          ? `${tempRange[0]}-${tempRange[tempRange.length - 1]}`
          : tempRange[0],
      );
    }

    // Append "5+" if selected
    if (selectedValues.includes('5+')) {
      formatted.push('5+');
    }

    return `${formatted.join(', ')} ${label?.toLowerCase()}`;
  };

  const resetSelection = () => {
    setSelectedBeds([]);
    setSelectedBaths([]);
  };

  // Handle search submission
  const handleSearch = async () => {
    // Close any open dropdowns
    setIsSearchFocused(false);
    setShowPropertyTypes(false);
    setShowBedsBaths(false);

    setIsLoading(true);

    try {
      // Search for properties with the current criteria
      const properties = await PropertyService.searchProperties(
        searchQuery,
        selectedPropertyType,
        selectedBeds,
        selectedBaths,
        activeTab,
      );

      // If no results found, show the NoResultsComponent
      // if (properties.length === 0 && NoResultsComponent) {
      //   // Render the NoResultsComponent in place
      //   return;
      // }

      // Create query parameters
      const params = new URLSearchParams();

      if (searchQuery) {
        params.append('q', searchQuery);
      }

      if (selectedPropertyType) {
        params.append('propertyType', selectedPropertyType);
      }

      if (selectedBeds.length > 0) {
        params.append('bedrooms', selectedBeds.join(','));
      }

      if (selectedBaths.length > 0) {
        params.append('bathrooms', selectedBaths.join(','));
      }

      // Determine destination based on active tab
      let destination = '/properties';

      switch (activeTab?.toLowerCase()) {
        case 'rent':
          destination = '/properties/rent';
          break;
        case 'buy':
          destination = '/properties/buy';
          break;
        case 'new projects':
          destination = '/properties/off-plan';
          break;
        case 'commercial':
          destination = '/properties/commercial';
          break;
      }

      // Navigate to the appropriate page with query parameters
      const queryString = params.toString();
      const url = queryString ? `${destination}?${queryString}` : destination;
      router.push(url);
    } catch (error) {
      console.error('Error during search:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Map tab names to their translated versions
  const tabNames = {
    Rent: t('rent'),
    Buy: t('buy'),
    'New projects': t('offPlan'),
    Commercial: t('commercial'),
  };

  const offeringType = (type) => {
    const types = { CS: 'Buy', RS: 'Buy', RR: 'Rent', CR: 'Rent' };
    return types[type.trim().toUpperCase()] || 'N/A';
  };

  const offPlanType = (type) => {
    if (type === 'off plan') {
      return 'Off-Plan-Secondary';
    } else if (type === 'off_plan_primary') {
      return 'Off-Plan-Primary';
    }
  };

  return (
    <div
      className='relative w-full max-w-6xl mx-auto px-4'
      ref={searchContainerRef}
    >
      {/* Search Container */}
      <div className='relative bg-white/95 dark:bg-gray-900/95 rounded-2xl shadow-2xl p-6 backdrop-blur-sm'>
        {/* Tabs */}
        <div className='flex mb-6 bg-gray-100 dark:bg-gray-800 rounded-full p-1 overflow-x-auto hide-scrollbar'>
          {['Rent', 'Buy', 'New projects', 'Commercial'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap
                ${
                  activeTab === tab
                    ? 'bg-white dark:bg-gray-900 text-primarycolor dark:text-secondarycolor shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-primarycolor dark:hover:text-secondarycolor'
                }`}
            >
              {tabNames[tab] || tab}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className='flex flex-col lg:flex-row gap-4 mb-4'>
          {/* Property Type Dropdown */}
          <div className='flex-1 relative' ref={propertyTypeRef}>
            <button
              onClick={() => setShowPropertyTypes(!showPropertyTypes)}
              onFocus={() => setIsSearchFocused(false)}
              className={`w-full lg:w-50 flex items-center justify-between px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primarycolor dark:hover:border-secondarycolor transition-color
               bg-white dark:bg-gray-900`}
            >
              <span className='flex items-center text-gray-700 dark:text-gray-300'>
                <Building2 className='w-5 h-5 mr-2' />
                {propertyTypes.find((pt) => pt.value === selectedPropertyType)
                  ?.label || t('propertyType')}
              </span>
              <motion.span
                animate={{ rotate: showPropertyTypes ? 180 : 0 }}
                className='text-gray-400'
              >
                ▼
              </motion.span>
            </button>

            <AnimatePresence>
              {showPropertyTypes && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className='absolute z-50 w-full mt-2 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto'
                >
                  {propertyTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setSelectedPropertyType(type.value);
                        setShowPropertyTypes(false);
                      }}
                      className='w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    >
                      {type.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Beds & Baths Dropdown */}
          <div className='flex-1 relative' ref={bedsBathsRef}>
            <button
              onClick={() => setShowBedsBaths(!showBedsBaths)}
              onFocus={() => setIsSearchFocused(false)}
              className='w-full lg:w-50 flex items-center justify-between px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-primarycolor dark:hover:border-secondarycolor transition-colors bg-white
              dark:bg-gray-900'
            >
              <span className='flex items-center text-gray-700 dark:text-gray-300'>
                <BedDouble className='w-5 h-5 mr-2' />
                {formatSelection(selectedBeds, t('beds'))} &{' '}
                {formatSelection(selectedBaths, t('baths'))}
              </span>
              <motion.span
                animate={{ rotate: showBedsBaths ? 180 : 0 }}
                className='text-gray-400'
              >
                ▼
              </motion.span>
            </button>

            <AnimatePresence>
              {showBedsBaths && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className='absolute z-50 w-72 mt-2 p-4 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700'
                >
                  <div className='mb-4'>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                      {t('bedrooms')}
                    </label>
                    <div className='flex flex-wrap gap-2'>
                      {bedOptions.map((bed) => (
                        <button
                          key={bed}
                          onClick={() => toggleSelection(bed, 'bed')}
                          className={`px-4 py-2 rounded-full text-sm ${
                            selectedBeds.includes(bed)
                              ? 'bg-primarycolor dark:bg-secondarycolor text-white dark:text-black'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                        >
                          {bed}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                      {t('bathrooms')}
                    </label>
                    <div className='flex flex-wrap gap-2'>
                      {bathOptions.map((bath) => (
                        <button
                          key={bath}
                          onClick={() => toggleSelection(bath, 'bath')}
                          className={`px-4 py-2 rounded-full text-sm ${
                            selectedBaths.includes(bath)
                              ? 'bg-primarycolor dark:bg-secondarycolor text-white dark:text-black'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                        >
                          {bath}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className='flex justify-between mt-4'>
                    <button
                      onClick={resetSelection}
                      className='px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700'
                    >
                      {t('reset')}
                    </button>
                    <button
                      onClick={() => setShowBedsBaths(false)}
                      className='px-4 py-2 bg-primarycolor dark:bg-secondarycolor text-white dark:text-black rounded-lg'
                    >
                      {t('ok')}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search Bar  */}
        <div className='flex flex-col lg:flex-col gap-4'>
          {/* Location Search */}
          <div className='flex-1 relative'>
            <div
              className={`relative flex items-center bg-white dark:bg-gray-900 rounded-full transition-all duration-500 border-2
              ${
                isSearchFocused
                  ? 'border-transparent bg-gradient-to-r from-[#FE2A53] via-[#38F5EF] to-[#FE2A53] p-[2px]'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className='bg-white dark:bg-gray-900 flex items-center rounded-full w-full p-2'>
                <Search className='w-5 h-5 text-gray-400 ml-2' />
                <input
                  ref={inputRef}
                  type='text'
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className='w-full text-lg bg-transparent outline-none px-3 py-2 border-none focus:ring-0 focus:border-transparent'
                />
                <button
                  onClick={startListening}
                  onFocus={() => setIsSearchFocused(true)}
                  className={`p-2 rounded-full transition-colors
                    ${
                      isListening
                        ? 'text-primarycolor dark:text-secondarycolor animate-pulse'
                        : 'text-gray-400 hover:text-primarycolor dark:hover:text-secondarycolor'
                    }`}
                  aria-label='Voice search'
                >
                  <Mic className='w-5 h-5' />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {isSearchFocused && searchQuery.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className='absolute w-full mt-2 bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden z-50 border border-gray-100 dark:border-gray-800'
                >
                  {isLoading && (
                    <div className='py-4 px-3 text-center'>
                      <div className='inline-flex items-center justify-center'>
                        <div className='w-4 h-4 rounded-full bg-primarycolor animate-pulse mr-2'></div>
                        <div
                          className='w-4 h-4 rounded-full bg-primarycolor animate-pulse mr-2'
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                        <div
                          className='w-4 h-4 rounded-full bg-primarycolor animate-pulse'
                          style={{ animationDelay: '0.4s' }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {!isLoading && locationSuggestions.length > 0 && (
                    <div className='p-2'>
                      <h3 className='text-sm font-semibold text-gray-500 dark:text-gray-400 px-3 py-2'>
                        Popular Locations
                      </h3>
                      <div className='max-h-60 overflow-y-auto space-y-1 pr-1'>
                        {locationSuggestions.map((location, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setSearchQuery(location);
                              setIsSearchFocused(false);
                            }}
                            className='flex items-center w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md'
                          >
                            <MapPin className='w-4 h-4 mr-2 text-primarycolor dark:text-secondarycolor' />
                            <span className='text-gray-700 dark:text-gray-200'>
                              {location}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isLoading && searchResults.length > 0 && (
                    <div className='border-t border-gray-200 dark:border-gray-700'>
                      <h3 className='text-sm font-semibold text-gray-500 dark:text-gray-400 px-3 py-2'>
                        Properties
                      </h3>
                      <div className='max-h-60 overflow-y-auto pr-1'>
                        {searchResults.map((property) => (
                          <Link
                            rel='preload'
                            key={property.id}
                            // href={`/properties/${property.slug}`}
                            href={`/properties/${
                              offPlanType(property?.completion_status)
                                ? 'off-plan'
                                : offeringType(property.offering_type)
                                    ?.toLowerCase()
                                    .replace(/_/g, '-')
                            }/${property.slug}`}
                            onClick={() => setIsSearchFocused(false)}
                            className='flex items-start gap-4 p-3 hover:bg-gray-100 dark:hover:bg-gray-800'
                          >
                            <div className='relative w-24 h-16 flex-shrink-0 overflow-hidden rounded-md'>
                              <Image
                                src={property.image || '/placeholder.svg'}
                                alt={property.title}
                                fill
                                unoptimized
                                className='object-cover'
                                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                                priority
                              />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <h4 className='text-sm font-medium text-gray-900 dark:text-white truncate'>
                                {property.title}
                              </h4>
                              <div className='flex items-center mt-1'>
                                <MapPin className='w-4 h-4 text-primarycolor dark:text-secondarycolor mr-1' />
                                <p className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                                  {property.location}
                                </p>
                              </div>
                              <p className='text-sm font-medium text-primarycolor dark:text-secondarycolor mt-1'>
                                {property.price}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No results message */}
                  {!isLoading &&
                    searchQuery.length > 0 &&
                    locationSuggestions.length === 0 &&
                    searchResults.length === 0 &&
                    (NoResultsComponent ? (
                      <NoResultsComponent
                        setSearchQuery={setSearchQuery}
                        inputRef={inputRef}
                      />
                    ) : (
                      <div className='py-6 px-4 text-center'>
                        <div className='inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 mb-3'>
                          <Search className='w-6 h-6 text-gray-400' />
                        </div>
                        <h3 className='text-base font-medium text-gray-900 dark:text-white mb-1'>
                          {t('noResultsFound') || 'No results found'}
                        </h3>
                        <p className='text-sm text-gray-500 dark:text-gray-400'>
                          {t('tryDifferentSearch') ||
                            'Try a different search term or browse our properties'}
                        </p>
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            inputRef.current?.focus();
                          }}
                          className='mt-3 text-sm font-medium text-primarycolor dark:text-secondarycolor hover:underline'
                        >
                          {t('clearSearch') || 'Clear search'}
                        </button>
                      </div>
                    ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Search Button */}
          {/* <button
            onClick={handleSearch}
            disabled={isLoading}
            className={`px-8 py-3 rounded-full font-medium transition-colors w-32 mx-auto block ${
              isLoading
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primarycolor hover:bg-secondarycolor text-white hover:text-black cursor-pointer'
            }`}
          >
            {isLoading ? (
              <span className='flex items-center justify-center'>
                <span className='w-2 h-2 bg-gray-500 rounded-full animate-bounce mr-1'></span>
                <span
                  className='w-2 h-2 bg-gray-500 rounded-full animate-bounce mr-1'
                  style={{ animationDelay: '0.2s' }}
                ></span>
                <span
                  className='w-2 h-2 bg-gray-500 rounded-full animate-bounce'
                  style={{ animationDelay: '0.4s' }}
                ></span>
              </span>
            ) : (
              t('search')
            )}
          </button> */}

          {/* Search Button */}
          <button
            onClick={handleSearch}
            // disabled={isLoading || locationSuggestions?.length === 0}
            disabled={isLoading}
            className={
              'px-8 py-3 rounded-full font-medium transition-colors w-32 mx-auto block bg-primarycolor hover:bg-secondarycolor text-white hover:text-black cursor-pointer'
            }
            // className={`px-8 py-3 rounded-full font-medium transition-colors w-32 mx-auto block ${
            //   isLoading || locationSuggestions?.length === 0
            //     ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            //     : 'bg-primarycolor hover:bg-secondarycolor text-white hover:text-black cursor-pointer'
            // }`}
          >
            {isLoading ? (
              <span className='flex items-center justify-center'>
                <span className='w-2 h-2 bg-white rounded-full animate-bounce mr-1'></span>
                <span
                  className='w-2 h-2 bg-white rounded-full animate-bounce mr-1'
                  style={{ animationDelay: '0.2s' }}
                ></span>
                <span
                  className='w-2 h-2 bg-white rounded-full animate-bounce'
                  style={{ animationDelay: '0.4s' }}
                ></span>
              </span>
            ) : (
              t('search')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
